import { AfterContentInit, ChangeDetectionStrategy, Component, ContentChild, EventEmitter, Input, OnInit, Output, TemplateRef, ViewChild, ViewEncapsulation } from '@angular/core';
import { getFileExtension } from '@common/project-files/util/files';
import { cache } from '@common/util/cache.operator';
import { StringService } from '@portal-core/general/services/string.service';
import { ProjectFilesTreeComponent } from '@portal-core/project-files/components/project-files-tree/project-files-tree.component';
import { ProjectFileFilterType } from '@portal-core/project-files/enums/project-files-filter-type.enum';
import { ProjectFolder } from '@portal-core/project-files/enums/project-folder.enum';
import { ProjectFilesTreeIndicator } from '@portal-core/project-files/models/project-files-tree-indicator.model';
import { ProjectUncommittedFile } from '@portal-core/project-files/models/project-uncommitted-file.model';
import { ProjectFilesService } from '@portal-core/project-files/services/project-files.service';
import { ProjectFilesTreeType } from '@portal-core/project-files/types/project-files-tree.type';
import { ProjectFileFlatNode } from '@portal-core/project-files/util/project-file-flat-node';
import { ProjectFilesTreeControl } from '@portal-core/project-files/util/project-files-tree-control';
import { ProjectData } from '@portal-core/project-files/workspace/models/project-data.model';
import { ProjectWorkspaceFilters } from '@portal-core/project-files/workspace/models/project-workspace-filters.model';
import { WorkspaceFileService } from '@portal-core/project-files/workspace/services/workspace-file.service';
import { ContextMenuItemsDirective } from '@portal-core/ui/grid/directives/context-menu-items/context-menu-items.directive';
import { InputObservable } from '@portal-core/util/input-observable.decorator';
import { combineLatest, map, Observable, of, Subscription, switchMap } from 'rxjs';

@Component({
  selector: 'mc-workspace-files-tree',
  templateUrl: './workspace-files-tree.component.html',
  styleUrls: ['./workspace-files-tree.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class WorkspaceFilesTreeComponent implements OnInit, AfterContentInit {

  @ViewChild(ProjectFilesTreeComponent, { static: false }) projectFilesTree: ProjectFilesTreeComponent;
  @ContentChild(ContextMenuItemsDirective) contextMenuItemsDirective: ContextMenuItemsDirective;

  @Input() createIndicators: boolean = true;
  /** The place in the tree to scroll to on open if a user hasn't selected a value yet. */
  @Input() defaultPath: string = null;
  @Input() projectData: ProjectData;
  @Input() projectFilesTreeType: ProjectFilesTreeType = 'default';
  @Input() filters: Observable<ProjectWorkspaceFilters>;
  @Input() filePath: string;
  @Input() projectFileIsDirty?: boolean = false;
  @Input() includeFiles: boolean = true;
  @Input() show404ErrorAsEmptyTree: boolean = false;
  @Input() fileFilter: string = null;
  @Input() fileFilterType: ProjectFileFilterType = null;
  @Input() treeClass?: string;
  @Input() rootFolder: string = ProjectFolder.Root;
  @Input() disableFolderCheckboxes: boolean = false;

  @InputObservable('projectData') projectData$: Observable<ProjectData>;
  @InputObservable('filters') filters$: Observable<ProjectWorkspaceFilters>;
  @InputObservable('filePath') filePath$: Observable<string>;

  @Output() workspaceFileFiltersChanged: EventEmitter<ProjectWorkspaceFilters> = new EventEmitter<ProjectWorkspaceFilters>();
  @Output() fileSelected: EventEmitter<ProjectFileFlatNode> = new EventEmitter<ProjectFileFlatNode>();
  @Output() pathFiltersChanged: EventEmitter<string[]> = new EventEmitter<string[]>();

  get branchName(): string {
    return this.projectData?.branchName;
  }

  get projectId(): number {
    return this.projectData?.projectId;
  }

  get selectedDirectoryPath(): string {
    return this.projectFilesTree?.selectedDirectoryPath;
  }

  set value(valueToSet: string[]) {
    if (this.projectFilesTree) {
      this.projectFilesTree.value = valueToSet;
    }

  }

  fileFilterTypeSubscription: Subscription;
  committedFilter: boolean = true;
  fileTreeUncommittedFiles$: Observable<ProjectUncommittedFile[]>;
  projectFilesTreeControl: ProjectFilesTreeControl;
  contextMenuItemsTemplate: TemplateRef<any>;

  constructor(private workspaceFileService: WorkspaceFileService,
    private projectFilesService: ProjectFilesService,
    private stringService: StringService) { }

  ngAfterContentInit() {
    this.contextMenuItemsTemplate = this.contextMenuItemsDirective && this.contextMenuItemsDirective.templateRef || null;
  }

  ngOnInit() {
    this.projectFilesTreeControl = new ProjectFilesTreeControl((filePath: string, branchName: string) => {
      return ['file', this.stringService.encodeURIComponent(branchName), this.stringService.encodeURIComponent(filePath)];
    });

    // change the files root folder and filter on the file filter type change
    this.fileFilterTypeSubscription = this.filters$.subscribe(filters => {

      // happens every time when core state is changed
      // so we should react only on the fileFilterType change
      if (this.fileFilterType === filters?.fileTypeFilter && this.committedFilter === filters?.fileStatusFilter) return;
      this.committedFilter = filters?.fileStatusFilter;
      // update the project tree
      if (filters?.fileTypeFilter || typeof this.committedFilter === 'boolean') {
        if (filters.fileTypeFilter) {
          this.fileFilterType = filters.fileTypeFilter;
          this.fileFilter = this.projectFilesService.fileTypes[filters.fileTypeFilter].filter;
          this.rootFolder = this.projectFilesService.fileTypes[filters.fileTypeFilter].restrictedToFolder;
        } else {
          this.fileFilterType = null;
          this.fileFilter = null;
          this.rootFolder = ProjectFolder.Root;
        }
        setTimeout(() => this.projectFilesTree?.changeRootFolder(this.rootFolder, this.fileFilter), 0);
      }
    });

    this.fileTreeUncommittedFiles$ = combineLatest([
      this.projectData$,
      this.filters$,
      this.workspaceFileService.workspaceItemsLoaded
    ]).pipe(
      switchMap(([projectData, filters, loaded]) => {
        const projectId = projectData?.projectId;
        const branchName = projectData?.branchName;
        if (typeof projectId === 'number' && typeof branchName === 'string' && typeof loaded === 'string') {
          return this.workspaceFileService.getItemsByProperties$('projectId', projectId, 'branchName', branchName).pipe(
            map((workspaceFiles) => {
              if (workspaceFiles?.length) {
                const filterExtensions = filters?.fileTypeFilter ? this.projectFilesService.fileTypes[filters.fileTypeFilter].extensions : [''];
                const allowAll = filterExtensions.includes('');
                let uncommittedFiles = [];
                workspaceFiles.forEach(workspaceFile => {
                  // Pre filter out files of the wrong type.
                  if (!allowAll && filterExtensions.find((ext) => ext === getFileExtension(workspaceFile.filePath)) === undefined) {
                    return;
                  }
                  const filePath = workspaceFile.filePath;
                  const indictator = { name: 'dot', color: 'mc-bg-info-2' };
                  const indicators = this.createIndicators ? [indictator] : [];
                  // For each file make sure the folders are also included.
                  this.getUncommittedFolders(uncommittedFiles, filePath, indictator, this.createIndicators);
                  uncommittedFiles.push({ filePath, indicators, isFolder: false });
                });

                return uncommittedFiles;
              }
              return [];
            })
          )
        } else {
          return of(null);
        }
      }),
      cache()
    );

  }

  getUncommittedFolders(uncommitedFiles: ProjectUncommittedFile[], filePath: string, indicator: ProjectFilesTreeIndicator, createIndicators: boolean = true) {
    const filePathSplit = filePath.split('/');
    let currentFolderCheck = '';

    filePathSplit.forEach((filePathPart, index) => {
      if (index != filePathSplit.length - 1) {
        currentFolderCheck += filePathPart + '/';

        const foundFolderIndicator = uncommitedFiles.find(item => item.filePath === currentFolderCheck.substring(0, currentFolderCheck.length - 1));

        if (!foundFolderIndicator) {
          uncommitedFiles.push({ filePath: currentFolderCheck.substring(0, currentFolderCheck.length - 1), indicators: createIndicators ? [indicator] : [], isFolder: true });
        } else if (createIndicators) {
          const foundIndicator = foundFolderIndicator.indicators.find(checkIndictator => checkIndictator.name === indicator.name);
          if (!foundIndicator) {
            foundFolderIndicator.indicators.push(indicator);
          }
        }
      }
    });
  }

  onWorkspaceFileFiltersChanged(filterData: ProjectWorkspaceFilters) {
    this.workspaceFileFiltersChanged.next(filterData);
  }

  onFileSelected(projectFileFlatNode: ProjectFileFlatNode) {
    this.fileSelected.next(projectFileFlatNode)
  }

  onPathFiltersChanged(pathFilters: string[]) {
    this.pathFiltersChanged.next(pathFilters);
  }

  hardReload() {
    this.projectFilesTree?.hardReload();
  }

  scrollToPath(filePath: string) {
    this.projectFilesTree?.scrollToPath(filePath);
  }

  addNodes(nodesToAdd: string[]): string[] {
    return this.projectFilesTree?.addNodes(nodesToAdd);
  }

  removeNodes(filePaths: string[], removeOnlyCollabFiles: boolean = true): string[] {
    return this.projectFilesTree?.removeNodes(filePaths, removeOnlyCollabFiles);
  }

  checkViewportSize() {
    this.projectFilesTree.checkViewportSize();
  }

  scrollToTop() {
    this.projectFilesTree.scrollToTop();
  }

  changeRootFolder(filePath: string) {
    this.projectFilesTree.changeRootFolder(filePath);
  }

  setSelection() {
    this.projectFilesTree.setSelection();
  }

  findNodeByPathAndUnselect(path: string, pathFilters: string[]) {
    this.projectFilesTree.findNodeByPathAndUnselect(path, pathFilters);
  }

}
